import { html, PropertyValues, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { EnumListEvents, EnumListItem, GroupType, SelectedOption } from '../../gzEnumList/types';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { GzEnumListWebComponent } from '../../gzEnumList';
import { WEB_COMPONENT_GZ_DROPDOWN_MENU } from '../../../webcomponents.constants';
import { GzButtonWebComponent } from '../../gzButton';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { LabelWidthValues, MenuPosition } from '../../../types/common/types';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { GzTooltipWebComponent } from '../../gzTooltip';
import { debounce } from 'lodash';

type EnumFilterValueType = (string | number)[];

export class GzEnumFilterWebComponent extends FormFieldInput<EnumFilterValueType> {
  @property({ type: String, attribute: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: EnumListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  confirmActionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @property({ type: String, attribute: true, reflect: true })
  noResultsInDefaultGroup;

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  /**
   * The maximum number of selections allowed in the enum filter. If the number of selected options exceeds this value,
   * the APPLY button will be disabled.
   */
  @property({ type: Number, attribute: true, reflect: true })
  maxSelections: number;

  /**
   * The tooltip text to display when the number of selected options exceeds the maxSelections value.
   */
  @property({ type: String, attribute: true, reflect: true })
  exceededLimitError = '';

  /**
   * The translated value for the list title
   */
  @property({ type: String, attribute: true, reflect: true })
  listTitle = '';

  /**
   * If this property is set to true, an 'ALL' checkbox will be displayed in the enumList, above the list items.
   */
  @property({ type: Boolean, reflect: true })
  canSelectAll = false;

  @property({ type: String, reflect: true })
  groupType: GroupType = GroupType.DEFAULT;

  @property({ type: String, attribute: true, reflect: true })
  allItemsLabel = '';

  /**
   * By default, the APPLY button is disabled if no options in the list are checked. Setting this property to true
   * will enable the APPLY button when no items are checked.
   */
  @property({ type: Boolean, reflect: true })
  allowSelectNone = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: String, attribute: true, reflect: true })
  errorMessage = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  readOnly = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: String, attribute: true, reflect: true })
  menuPosition = MenuPosition.LEFT;

  @property({ type: Number, attribute: true, reflect: true })
  searchMaxLength;

  @property({ type: Boolean, attribute: true, reflect: true })
  shouldAlignVertically = false;

  @property({ type: Number, attribute: true, reflect: true })
  verticalAlignThreshold = 0.35;

  @property({ type: Boolean, reflect: true })
  isClearable = false;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  dropdownMenu!: GzDropdownMenuWebComponent;

  @query('#multiple-selection-list')
  protected readonly enumList!: GzEnumListWebComponent;

  @query('#content')
  private content: HTMLElement;

  @query('#confirmActionButton')
  private confirmActionButton!: GzButtonWebComponent;

  @query('#filterFooter')
  private filterFooter: HTMLElement;

  @query('#applyTooltip')
  private readonly applyTooltip: GzTooltipWebComponent;

  @state()
  private selectedOptions: EnumListItem[];

  static get styles() {
    return styles;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    /**
     * When the filteroptions property update is finished, get the checked options from the enumList and update the
     * dropdown menu label
     */
    if (name === 'filteroptions' && newVal) {
      this.updateComplete.then(() => {
        this.handleFilterOptionsUpdate();
      });
    }

    if (name === 'labelwidth' && newVal) {
      this.updateComplete.then(() => {
        this.dropdownMenu.labelWidth = newVal;
      });
    }
  }

  protected updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('maxSelections')) {
      this.updateComplete.then(() => {
        this.handleFilterOptionsUpdate();
      });
    }
  }

  private handleFilterOptionsUpdate(): void {
    this.ensureMaxSelections();
    this.selectedOptions = this.getSelectedFilterOptions();
    this.updateDropdownLabelWithSelectedValues(this.selectedOptions);
    this.setActionButtonDisabledState(isEmpty(this.selectedOptions));
  }

  private ensureMaxSelections(): void {
    if (this.isExceedingMaxSelections()) {
      let selectedCount = 0;
      this.filterOptions = this.filterOptions.map(option => {
        if (selectedCount < this.maxSelections && option.selected) {
          selectedCount++;
          return option;
        } else {
          return {
            ...option,
            selected: false,
          };
        }
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(EnumListEvents.OPTIONS_FILTERED, this.handleFilterOptionsEvent.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener(EnumListEvents.OPTIONS_FILTERED, this.handleFilterOptionsEvent);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <gz-dropdown-menu
        ?canDelete="${this.shouldDisplayClearButton()}"
        label="${this.label}"
        labelWidth=${this.labelWidth}"
        @open="${this.handleDropdownOpen}"
        @close="${this.handleDropdownClose}"
        @clear="${this.handleClearFilter}"
        placeholder="${this.placeholder}"
        ?shouldAlignVertically="${this.shouldAlignVertically}"
        .verticalAlignThreshold="${this.verticalAlignThreshold}"
        ?isError="${this.isError}"
        errorTooltipText="${this.errorMessage}"
        ?readOnly="${this.readOnly}"
        menuPosition="${this.menuPosition}"
        ?autoAdjustMenuPosition="${this.autoAdjustMenuPosition}"
      >
        ${this.renderDropdownMenuContent()}
      </gz-dropdown-menu>
    `;
  }

  public setFormValue(value: EnumFilterValueType): void {
    if (Array.isArray(value)) {
      // For each given value at input
      this.filterOptions = this.filterOptions.map(filterOption => {
        filterOption.selected = value.includes(filterOption.value);
        return filterOption;
      });
    }
  }

  public getFormValue(): EnumFilterValueType {
    const checkedOptions = this.filterOptions.filter(option => option.selected);
    return checkedOptions.map(item => item.value);
  }

  private shouldDisplayClearButton(): boolean {
    if (!this.isClearable) {
      return false;
    }

    return !isEmpty(this.selectedOptions);
  }

  private renderDropdownMenuContent() {
    if (this.isLoading) {
      return this.renderLoadingTemplate();
    }
    if (this.isError) {
      return this.renderErrorTemplate();
    }
    const classes = {
      'filter--footer': true,
      [ElementClass.HIDDEN]: this.filterOptions.length === 0,
    };

    return html` <div class="filter--content" id="content">${this.renderEnumList()}</div>
      <div id="filterFooter" class=${classMap(classes)}>
        <gz-tooltip id="applyTooltip">
          <gz-button
            id="confirmActionButton"
            @click="${this.handleConfirmSelection}"
            ?disabled="${isEmpty(this.filterOptions) || !this.allowSelectNone || this.isExceedingMaxSelections()}"
            type="quiet"
            color="primary"
            buttonHeight="small"
            buttonFontSize="small"
          >
            ${this.confirmActionButtonString}
          </gz-button>
        </gz-tooltip>
      </div>`;
  }

  private isExceedingMaxSelections(): boolean {
    return this.maxSelections !== undefined && this.enumList?.getCheckedOptions()?.length > this.maxSelections;
  }

  private renderLoadingTemplate() {
    return html` <div class="spinner">
      <gz-progress-spinner spinnerwidth="small"></gz-progress-spinner>
    </div>`;
  }

  private renderErrorTemplate() {
    return html` <div class="dropdown-error">${this.errorMessage}</div>`;
  }

  protected renderEnumList(): TemplateResult {
    return html` <gz-enum-list
      id="multiple-selection-list"
      .filterOptions="${this.filterOptions}"
      searchPlaceholderString="${this.searchPlaceholderString}"
      noSearchResultsString="${this.noSearchResultsString}"
      noResultsInDefaultGroup="${ifDefined(this.noResultsInDefaultGroup)}"
      .listTitle="${this.listTitle}"
      groupType="${this.groupType}"
      .canSelectAll="${this.canSelectAll}"
      allItemsLabel="${this.allItemsLabel}"
      @optionChanged="${this.handleListOptionsChange}"
      @optionsFiltered="${debounce(e => this.handleListOptionsChange())}"
      .searchMaxLength="${ifDefined(this.searchMaxLength)}"
      class="enum-filter-list"
    ></gz-enum-list>`;
  }

  protected updateDropdownLabelWithSelectedValues(selectedOptions: SelectedOption[]) {
    /**
     * Don't display the selected values if the label width is small. See 'More' column in the design file
     * https://xd.adobe.com/view/c35ce011-09c5-43f1-6c2a-106f66a46de0-b56d/screen/a9ced8a4-e3b8-4d11-b65b-5eae4a51db50/specs/
     */
    this.dropdownMenu.label = this.label ?? '';

    if (
      isEmpty(selectedOptions) ||
      this.dropdownMenu.labelWidth === LabelWidthValues.SMALL ||
      this.dropdownMenu.labelWidth === LabelWidthValues.AUTO
    ) {
      this.dropdownMenu.selectedValuesStrings = '';
    } else {
      const allSelected = (this.filterOptions || []).length === selectedOptions.length;
      const selectedOptionsText = allSelected
        ? this.allItemsLabel
        : selectedOptions.map(option => option.label).join(', ');
      this.dropdownMenu.selectedValuesStrings = selectedOptionsText;
    }
  }

  /**
   * When the dropdown is closed clear the search input value and re-set the enum list options to their initial state
   */
  protected handleDropdownClose(e: Event) {
    if (!this.isError && !this.isLoading) {
      this.enumList.searchInput.clearInputValue(e);
      this.enumList.filterOptions = [...this.filterOptions];
      this.dispatchEvent(new CustomEvent('close'));
    }
  }

  private handleConfirmSelection() {
    /**
     * When the Apply button is clicked:
     * 1. update the filter with the new values received from the enum list component
     * 2. get the selected checkboxes from the enum list component
     * 3. update the dropdown label with the new selected options
     * 4. update the selectedOptions property on the enumList component
     * 5. emit the selection event
     * 6. close the menu
     */

    this.filterOptions = this.enumList.filterOptions;
    const checkedOptions = this.enumList.getCheckedOptions();
    this.updateDropdownLabelWithSelectedValues(checkedOptions);
    this.enumList.setSelectedOptions(checkedOptions);
    this.enumList.emitConfirmSelectionEvent();
    this.dropdownMenu.closeDropdownMenu();
  }

  public getCheckedValues(): (number | string)[] {
    const checkedOptions = this.enumList.getCheckedOptions();
    return checkedOptions.map(item => item.value);
  }

  /**
   * When the dropdown is open, check if any option has the 'selected' state and update the checkboxes states
   * accordingly
   */
  protected handleDropdownOpen() {
    if (this.isLoading || this.isError) {
      this.dispatchEvent(new CustomEvent('invalidOpen'));
      return;
    }
    this.enumList.matchCheckboxesToSelectedOptions();
    this.enumList.updateSelectAllCheckboxesState();
    this.setActionButtonDisabledState(isEmpty(this.enumList.getCheckedOptions()));
    this.dispatchEvent(new CustomEvent('open'));
  }

  private getSelectedFilterOptions(): EnumListItem[] {
    const selectedOptions = this.filterOptions.filter(option => option.selected);
    if (this.isExceedingMaxSelections()) {
      return selectedOptions.slice(0, this.maxSelections);
    }

    return selectedOptions;
  }

  protected setActionButtonDisabledState(isDisabled: boolean) {
    if (this.isExceedingMaxSelections()) {
      this.confirmActionButton.disabled = true;
      return;
    }

    if (this.allowSelectNone || this.isError) {
      this.confirmActionButton.disabled = false;
    } else {
      this.confirmActionButton.disabled = isDisabled;
    }
  }

  private handleFilterOptionsEvent(e: CustomEvent) {
    if (this.filterOptions.length === 0) {
      return;
    }
    if (e.detail.resultsFound) {
      this.setActionButtonDisabledState(isEmpty(this.enumList.getCheckedOptions()));
      elementClassListRemove(this.filterFooter, ElementClass.HIDDEN);
    } else {
      this.setActionButtonDisabledState(true);
      if (!this.filterFooter.classList.contains(ElementClass.HIDDEN)) {
        elementClassListAdd(this.filterFooter, ElementClass.HIDDEN);
      }
    }
  }

  protected handleListOptionsChange() {
    this.setButtonTooltipText();
    this.setActionButtonDisabledState(isEmpty(this.enumList.getCheckedOptions()) || this.isExceedingMaxSelections());
  }

  private setButtonTooltipText() {
    if (this.applyTooltip) {
      this.applyTooltip.tooltipText = this.isExceedingMaxSelections() ? this.exceededLimitError : '';
    }
  }

  private uncheckAllOptions(): void {
    this.filterOptions = this.filterOptions.map((option: EnumListItem) => {
      return {
        ...option,
        selected: false,
      };
    });
  }

  private handleClearFilter(): void {
    this.uncheckAllOptions();
    this.enumList.dispatchEvent(
      new CustomEvent(EnumListEvents.SELECTION_CHANGE, {
        detail: {
          selectedValues: [],
        },
        bubbles: true,
        composed: true,
      })
    );
  }
}
